/* eslint-disable css-modules/no-unused-class, no-nested-ternary */

import { FC, useState, useRef, useEffect } from 'react';
import { translate } from 'translations';
import { useNavigate } from 'react-router-dom';
import { selectIsLeanUser, selectIsCxUniversity, selectTenantName } from '@redux/slices/userSlice';
import { useSelector } from 'react-redux';
import { Icon, MenuItems } from 'learning-panel-components';
import { KeyboardArrowDownIcon } from '../../../uiLibraryImports';
import styles from './topBar.module.scss';
import NotificationsContainer from '../notifications/notificationsContainer';
import UserSection from './userSection';
import LeanTopRightSection from './leanTopRightSection';
import SearchContainer from './searchContainer';

const TopBar: FC<TopBarProps> = (props: TopBarProps) => {
    const {
        imageSrc,
        userName,
        onLogout,
        selectedTab,
        setSelectedTab,
        setIsAccountSettingsOpen,
        setIsContactSupportOpen,
        newNotification,
        shouldPlayGlow,
        isLeanUser,
        devCoins,
        userRank,
        isPLG,
        setVideoTutorialUuid,
        learningPathTopBadgeData
    } = props;
    const [showResourcesDropDown, setShowResourcesDropDown] = useState<boolean>(false);
    const [showNotifications, setShowNotifications] = useState<boolean>(false);
    const [narrowScreen, setNarrowScreen] = useState<boolean>(false);
    const navigate = useNavigate();

    const resourcesRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const notificationsRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const rightSideRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const lean = useSelector(selectIsLeanUser);
    const isCxUniversity = useSelector(selectIsCxUniversity);
    const tenantName = useSelector(selectTenantName);
    const defaultFlavor = 'full';
    const flavor = lean ? 'lean' : isCxUniversity ? 'cxUniversity' : defaultFlavor;

    useEffect(() => {
        const handleResize = () => {
            setNarrowScreen(window.innerWidth < 875);
        };
        window.addEventListener('resize', handleResize);
        setNarrowScreen(window.innerWidth < 875);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (showResourcesDropDown && resourcesRef.current && !resourcesRef.current.contains(e.target)) {
                setShowResourcesDropDown(false);
            }
            if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(e.target)) {
                if ((rightSideRef.current && !rightSideRef.current.contains(e.target)) || !rightSideRef.current) {
                    setShowNotifications(false);
                }
            }
        };

        document.addEventListener('mousedown', checkIfClickedOutside);

        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [showResourcesDropDown, showNotifications]);

    const tabs: TopBarTab[] = [
        {
            name: 'home',
            title: translate('Home'),
            selectedTabs: ['home'],
            iconName: 'homeIcon',
            availableOnLean: true,
            flavors: ['lean', 'full', 'cxUniversity']
        },
        {
            name: 'learn',
            title: translate('Discover'),
            selectedTabs: ['learn', 'course', 'skillTree', 'learningPath'],
            iconName: 'learnIcon',
            availableOnLean: true,
            flavors: ['lean', 'full', 'cxUniversity']
        },
        {
            name: 'play',
            title: translate('Play'),
            selectedTabs: ['play', 'tournament'],
            iconName: 'playIcon',
            availableOnLean: false,
            flavors: ['lean', 'full']
        },
        {
            name: 'test',
            title: translate('Test'),
            selectedTabs: ['test'],
            iconName: 'testIcon',
            availableOnLean: false,
            flavors: ['lean', 'full']
        }
    ];

    const availableTabs = tabs.filter(tab => tab.flavors.includes(flavor));

    const userSectionProps = {
        onLogout,
        userName,
        setIsAccountSettingsOpen,
        setIsContactSupportOpen,
        narrowScreen,
        imageSrc
    };

    return (
        <div className={styles['top-bar']} style={isLeanUser ? { top: '3.3125rem' } : undefined}>
            <div className={styles['left-side']}>
                <img
                    src={`${process.env.PUBLIC_URL}/images/cxSmallLogo.svg`}
                    className={styles.logo}
                    alt="Codebashing Logo"
                />

                {!narrowScreen &&
                    (isCxUniversity ? (
                        <div className={styles['cx-uni-top']}>
                            <div className={styles['logo-divider']} />
                            <img
                                src={`${process.env.PUBLIC_URL}/images/cxUniversityLogo.svg`}
                                className={styles['cx-uni-logo']}
                                alt="CX University Logo"
                            />
                        </div>
                    ) : tenantName.toLowerCase() === 'owasp' ? (
                        <div className={styles['cx-uni-top']}>
                            <div className={styles['logo-divider']} />
                            <img
                                src={`${process.env.PUBLIC_URL}/images/owaspLogo.svg`}
                                className={styles['cx-uni-logo']}
                                alt="Owasp Logo"
                            />
                        </div>
                    ) : (
                        <></>
                    ))}
            </div>
            <div className={styles['mid-side']}>
                <div className={styles.tabs}>
                    {availableTabs.map(
                        tab =>
                            (!isLeanUser || (isLeanUser && tab.availableOnLean)) && (
                                <>
                                    <div
                                        className={shouldPlayGlow && tab.name === 'play' && styles['active-tournament']}
                                        key={tab.name}
                                    >
                                        {shouldPlayGlow && tab.name === 'play' && <div className={styles.dot}></div>}
                                        <div
                                            id={`${tab.name}-tab`}
                                            key={tab.name}
                                            className={
                                                tab.selectedTabs.includes(selectedTab)
                                                    ? styles['selected-tab']
                                                    : styles.tab
                                            }
                                            onClick={() => setSelectedTab(tab.name)}
                                        >
                                            {!narrowScreen ? (
                                                shouldPlayGlow && tab.name === 'play' ? (
                                                    <div className={styles['tournament-active-wrapper']}>
                                                        <Icon name={'tournamentIcon'} key={tab.name}></Icon>
                                                        {tab.title}
                                                    </div>
                                                ) : (
                                                    tab.title
                                                )
                                            ) : (
                                                <Icon
                                                    name={tab.iconName}
                                                    className={styles['tab-icon']}
                                                    key={tab.name}
                                                ></Icon>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.divider} />
                                </>
                            )
                    )}
                </div>
                <div className={styles['resources-wrapper']} ref={resourcesRef}>
                    <div
                        className={styles['resources-list-wrapper']}
                        onClick={() => setShowResourcesDropDown(!showResourcesDropDown)}
                    >
                        <Icon name="checkmarx" className={styles.icon} />
                        {!narrowScreen && <div>{translate('Resources')}</div>}

                        <KeyboardArrowDownIcon className={styles['arrow-icon']} />
                    </div>
                    {showResourcesDropDown && (
                        <div className={styles['resources-actions-wrapper']}>
                            <a
                                href="https://checkmarx.com/blog/"
                                target="_blank"
                                rel="noreferrer"
                                className={styles['resources-action-wrapper']}
                            >
                                <div>{translate('Checkmarx Blog')}</div>
                                <Icon name="openInNewTab" className={styles.icon} />
                            </a>
                            <a
                                href="https://checkmarx.com/resource/documents/en/34965-15304-checkmarx-codebashing.html"
                                target="_blank"
                                rel="noreferrer"
                                className={styles['resources-action-wrapper']}
                            >
                                <div>{translate('Codebashing Documentation')}</div>
                                <Icon name="openInNewTab" className={styles.icon} />
                            </a>
                            <a
                                href="https://checkmarx.com/resources/ebooks-and-whitepapers/"
                                target="_blank"
                                rel="noreferrer"
                                className={styles['resources-action-wrapper']}
                            >
                                <div>{translate('AppSec University')}</div>
                                <Icon name="openInNewTab" className={styles.icon} />
                            </a>
                        </div>
                    )}
                </div>
                <div className={styles.divider} />
                <SearchContainer setVideoTutorialUuid={setVideoTutorialUuid} />
            </div>
            <div className={styles['right-side']}>
                {learningPathTopBadgeData && learningPathTopBadgeData?.name && (
                    <div
                        className={styles['badge-wrapper']}
                        onClick={() => navigate(`/app/learningPath?uuid=${learningPathTopBadgeData.uuid}`)}
                    >
                        <img
                            src={`${process.env.PUBLIC_URL}/images/learningPathBadges/${learningPathTopBadgeData?.name}.svg`}
                            className={styles.badge}
                            alt=""
                        />

                        <div className={styles['badge-progress-text']}>{`${learningPathTopBadgeData?.progress}%`}</div>
                    </div>
                )}
                {isLeanUser ? (
                    <LeanTopRightSection {...userSectionProps} devCoins={devCoins} userRank={userRank} isPLG={isPLG} />
                ) : (
                    <UserSection {...userSectionProps} />
                )}
                <div
                    className={
                        showNotifications ? styles['notification-wrapper-clicked'] : styles['notification-wrapper']
                    }
                    id="notification-wrapper"
                >
                    <Icon
                        key={newNotification ? 'newNotification' : 'notification'}
                        name={newNotification ? 'newNotification' : 'notification'}
                        className={styles.icon}
                        onClick={() => setShowNotifications(!showNotifications)}
                    />
                </div>
                {showNotifications && (
                    <NotificationsContainer refFunction={notificationsRef} rightSideRef={rightSideRef} />
                )}
            </div>
        </div>
    );
};

interface TopBarProps {
    imageSrc: string;
    userName: string;
    onLogout: Function;
    selectedTab: keyof typeof MenuItems;
    setSelectedTab: Function;
    setIsAccountSettingsOpen: Function;
    setIsContactSupportOpen: Function;
    newNotification: boolean;
    shouldPlayGlow: boolean;
    isLeanUser: boolean;
    isPLG: boolean;
    devCoins: number;
    userRank: number;
    setVideoTutorialUuid: Function;
    learningPathTopBadgeData?: { uuid: string; progress: number; name: string };
}
export type Flavor = 'lean' | 'cxUniversity' | 'full';

export interface TopBarTab {
    name: string;
    title: string;
    selectedTabs: (keyof typeof MenuItems)[];
    iconName: string;
    availableOnLean: boolean;
    flavors: Flavor[];
}

export default TopBar;
